/**
 * M10 P2 (plan 2026-10-10-m10-p2 §3.1 P2c1): the AI hull catalogue. A hull mix per (tooling faction × buyer
 * faction × job) in flat typed arrays, and two pick functions that reproduce today's two draw arithmetics bit for
 * bit: `pickCatalogueHull` walks like `pickWeighted` (galaxy/seed.ts, subtract) and `pickCatalogueHullCum` like the
 * convoy's `pickHull` (agents/convoy.ts, cumulative). The caller draws `r01 = nextFloat(rng)`, so each site keeps
 * its own rng and draw condition (spec §6.2).
 *
 * In P2 every row copied the `roles.json` mix for its job. M10 P4a1b (plan 2026-10-11-m10-p4a §3.2): a row now holds
 * only the mix hulls its tooling may build (an `export` pattern, the tooling's own line, or a line it holds a licence
 * for); an outlaw buyer gets only `outlawRule` export patterns; an L-or-above hull's weight is × `flagship.lWeight`
 * and a zero weight drops the hull. On the shipped data every AI mix hull is an S/M export pattern, so every row still
 * equals the `roles.json` mix. The jobs are the seven `ROLE_IDS` plus `escort` (the convoy's `escortHulls`). Both
 * faction axes are sized by `caps.factionCapacity`, so a player-slot or later faction index never reads past the arrays.
 *
 * Imports only `types.ts`, `hullLines.ts` (itself only `types.ts`, zod and JSON), zod, its JSON and type-only modules:
 * `agents/jobs.ts` will import this module (P2c2), so importing it back (for `ROLE_COUNT`) would be a module cycle with
 * a module-scope read.
 */
import { z } from 'zod'
import catalogueJson from '../data/strategic/catalogue.json'
import { PLAYER_SLOT, ROLE_IDS, type StrategicState } from './types'
import type { StrategicData } from './data'
import { HULL_BAND_IDS } from './hullLines'
import type { RolesFile } from './schemas'

const catalogueFileSchema = z.strictObject({
  comment: z.string(),
  /** Off: `catalogueOf` returns null and every call site keeps its `roles.json` line. */
  enabled: z.boolean(),
  /** The most positive-weight hulls one row holds; a larger mix throws at build. */
  slots: z.number().int().min(1).max(255),
})
export type CatalogueFile = z.infer<typeof catalogueFileSchema>

export const CATALOGUE_FILE: CatalogueFile = catalogueFileSchema.parse(catalogueJson)

/** Per-row hull capacity ([P] 8, `catalogue.json slots`). */
export const CATALOGUE_SLOTS = CATALOGUE_FILE.slots

/** The catalogue jobs: the seven roles in `ROLE` order, then the convoy escort. */
export const CATALOGUE_JOBS = [...ROLE_IDS, 'escort'] as const
export type CatalogueJob = (typeof CATALOGUE_JOBS)[number]
export const JOB_COUNT = ROLE_IDS.length + 1
/** The escort job's index (after the seven `ROLE` values). */
export const JOB_ESCORT = ROLE_IDS.length

/**
 * One set of weight rows, `row × slots + k` per key. M10 P4b5a: a catalogue holds two sets, `all` (the catalogue
 * itself) and `smOnly`, each with its own `len`, `total` and `cum`, so neither row's round-off guard can return a hull
 * that only the other row holds (review M1).
 */
export interface CatalogueRows {
  /** `row × slots + k`: the hull index (`hullIndex.get(id) ?? 0`) of the row's k-th positive-weight key. */
  readonly hull: Int16Array
  /** `row × slots + k`: that key's weight. */
  readonly weight: Float64Array
  /** `row × slots + k`: the running sum of the row's weights up to and including k (the convoy's `cum`). */
  readonly cum: Float64Array
  /** Per row: how many keys it holds. */
  readonly len: Uint8Array
  /** Per row: the sum of its positive weights, in key order (as `pickWeighted` sums it). */
  readonly total: Float64Array
}

/** The catalogue's own rows are the `all` rows (with each L entry × `flagship.lWeight`); `smOnly` drops the L entries. */
export interface Catalogue extends CatalogueRows {
  /** Both faction axes' size (`caps.factionCapacity`). */
  readonly factionCapacity: number
  /** Hulls per row (`CATALOGUE_SLOTS`). */
  readonly slots: number
  /** M10 P4b5a: the rows without their L-or-above entries (what a draw reads unless it passes `allowL`). */
  readonly smOnly: CatalogueRows
  /** M10 P4b5a: per row, 1 when the `all` row holds an L-or-above hull (`rowHasL`). */
  readonly hasL: Uint8Array
}

/** The row of (tooling, buyer, job). */
export function catalogueRow(cat: Catalogue, tooling: number, buyer: number, job: number): number {
  return (tooling * cat.factionCapacity + buyer) * JOB_COUNT + job
}

function mixOf(roles: RolesFile, job: number): Readonly<Record<string, number>> {
  return job === JOB_ESCORT ? roles.convoy.escortHulls : roles[ROLE_IDS[job]].hulls
}

/**
 * M10 P4a1b: what the tooling rows read. `catalogueOf` passes the `StrategicData` itself; tests pass a copy with a
 * changed field. `charter.outlaw` is per faction (the player slot is outlaw until founding, `registry.ts`).
 */
export type CatalogueLines = Pick<StrategicData, 'hullLines' | 'hullMaker' | 'hullBand' | 'hullFlags' | 'hullLine' | 'lineIds' | 'hulls' | 'factions'> & {
  readonly charter: Pick<StrategicData['charter'], 'outlaw'>
}

/** `HULL_FLAG.export` and `HULL_NONE` (`data.ts`; not imported, a test pins them equal). */
export const CATALOGUE_EXPORT_BIT = 1
export const CATALOGUE_HULL_NONE = 255
/** `HULL_FLAG.retired` (`data.ts`; M10 P4b1a2, not imported, a test pins it equal): a retired hull is in no row. */
export const CATALOGUE_RETIRED_BIT = 16
/** The first band whose weight is × `flagship.lWeight` (L and above). */
const L_BAND = HULL_BAND_IDS.indexOf('L')

/** A cell's source mix: its positive-weight keys as hull indices and weights, in insertion order (build time only). */
interface SourceMix {
  readonly hull: Int16Array
  readonly weight: Float64Array
  readonly len: number
}

function sourceMixOf(mix: Readonly<Record<string, number>>, hullIndex: ReadonlyMap<string, number>, slots: number, what: string): SourceMix {
  const ids = Object.keys(mix).filter((id) => (mix[id] ?? 0) > 0)
  if (ids.length === 0) throw new Error(`catalogue: ${what} has no positive weight`)
  if (ids.length > slots) throw new Error(`catalogue: ${what} has ${ids.length} positive hulls, more than the ${slots} slots`)
  const hull = new Int16Array(ids.length)
  const weight = new Float64Array(ids.length)
  for (let k = 0; k < ids.length; k++) {
    hull[k] = hullIndex.get(ids[k]) ?? 0
    weight[k] = mix[ids[k]] ?? 0
  }
  return { hull, weight, len: ids.length }
}

/** The tooling and outlaw tables `buildCatalogue` reads per row (build time only). */
interface LineRules {
  readonly lines: CatalogueLines
  /** `tooling × lineCount + line`: 1 when the tooling holds a licence for the line. */
  readonly licensed: Uint8Array
  readonly lineCount: number
  /** Bit per band of `outlawRule.bands`. */
  readonly outlawBands: number
  readonly lWeight: number
  /** M10 P4b5a: `buyer × JOB_COUNT + job`: the buyer's `hull-lines buyerMixes` mix for the job, else null. */
  readonly buyerMix: readonly (SourceMix | null)[]
}

function lineRulesOf(lines: CatalogueLines, factionCapacity: number, hullIndex: ReadonlyMap<string, number>, slots: number): LineRules {
  const lineCount = lines.lineIds.length
  const licensed = new Uint8Array(factionCapacity * lineCount)
  const ids = lines.factions.factions.map((f) => f.id)
  for (const l of lines.hullLines.licences) {
    const t = ids.indexOf(l.licensee)
    const li = lines.lineIds.indexOf(l.line)
    if (t >= 0 && t < factionCapacity && li >= 0) licensed[t * lineCount + li] = 1
  }
  let outlawBands = 0
  for (const b of lines.hullLines.outlawRule.bands) outlawBands |= 1 << HULL_BAND_IDS.indexOf(b)
  const buyerMix: (SourceMix | null)[] = new Array<SourceMix | null>(factionCapacity * JOB_COUNT).fill(null)
  for (const [buyer, jobs] of Object.entries(lines.hullLines.buyerMixes ?? {})) {
    const b = (ids as readonly string[]).indexOf(buyer)
    if (b < 0 || b >= factionCapacity) continue
    for (const [job, mix] of Object.entries(jobs ?? {})) {
      const j = (ROLE_IDS as readonly string[]).indexOf(job)
      if (j < 0 || mix === undefined) continue
      buyerMix[b * JOB_COUNT + j] = sourceMixOf(mix, hullIndex, slots, `buyerMixes.${buyer}.${job}`)
    }
  }
  return { lines, licensed, lineCount, outlawBands, lWeight: lines.hullLines.flagship.lWeight, buyerMix }
}

/** Tooling `t` may build hull `h`: an export pattern, its own line, or a line it holds a licence for. */
function toolingBuilds(r: LineRules, t: number, h: number): boolean {
  const l = r.lines
  if (((l.hullFlags[h] ?? 0) & CATALOGUE_EXPORT_BIT) !== 0) return true
  if (l.hullMaker[h] === t) return true
  const line = l.hullLine[h] ?? CATALOGUE_HULL_NONE
  return line !== CATALOGUE_HULL_NONE && r.licensed[t * r.lineCount + line] === 1
}

/** M10 P4b1a2: hull `h` is retired (kept for old saves, built by no yard), so it is in no row and no fallback. */
function retired(r: LineRules, h: number): boolean {
  return ((r.lines.hullFlags[h] ?? 0) & CATALOGUE_RETIRED_BIT) !== 0
}

/** The outlaw rule (spec §4.8): an export pattern (when `exportOnly`) of an `outlawRule` band. */
function outlawMayBuy(r: LineRules, h: number): boolean {
  const l = r.lines
  if (l.hullLines.outlawRule.exportOnly && ((l.hullFlags[h] ?? 0) & CATALOGUE_EXPORT_BIT) === 0) return false
  const band = l.hullBand[h] ?? CATALOGUE_HULL_NONE
  return band !== CATALOGUE_HULL_NONE && ((r.outlawBands >> band) & 1) === 1
}

/** Hull `h` is L or above (a hull outside hull-lines is not). */
function isL(r: LineRules, h: number): boolean {
  const band = r.lines.hullBand[h] ?? CATALOGUE_HULL_NONE
  return band !== CATALOGUE_HULL_NONE && band >= L_BAND
}

/** Hull `h`'s weight factor in the `all` rows: `flagship.lWeight` from band L up, else 1. */
function bandWeight(r: LineRules, h: number): number {
  return isL(r, h) ? r.lWeight : 1
}

/** Hull `h` flies `job`: its `hulls.json roles`; the escort job reads its `escort` flag (as `data.ts` checks the mixes). */
function fliesJob(r: LineRules, h: number, job: number): boolean {
  const row = r.lines.hulls.hulls[h]
  if (row === undefined) return false
  return job === JOB_ESCORT ? row.escort : row.roles.includes(ROLE_IDS[job])
}

/**
 * Builds the catalogue. Each job's reference row is its `roles.json` mix, positive-weight keys in insertion order (zero
 * weights are skipped, as both mirrored functions skip them). Throws when a mix has more positive keys than
 * `CATALOGUE_SLOTS` or no positive weight.
 *
 * Without `lines` (the P2 build) every (tooling × buyer) pair copies the reference rows, and `smOnly` equals `all` (no
 * band is known). With `lines` (M10 P4a1b; `catalogueOf` passes them) the row of (tooling t, buyer b, job j) keeps, in
 * mix order, each mix hull h that t builds (`export`, `hullMaker[h] === t`, or a licence of t for `hullLine[h]`); for
 * an outlaw b only `outlawRule` hulls; an L-or-above weight is × `flagship.lWeight` and a weight of 0 is dropped (not
 * kept at 0, so the subtract walk's round-off guard can't return it). A tooling that is no maker (the player slot, the
 * guardian, an index past the factions) holds no line and no licence, so its rows are the export patterns. An empty
 * row falls back to the job's export patterns (the deadlock guard): every `export` hull that flies the job, in hull
 * order, at weight 1 (review m5: 1 : 1, never the mix's weights), under the same outlaw rule and L weight; none
 * throws. M10 P4b1a2: a retired hull (`HULL_FLAG.retired`) is dropped from every row and every fallback. A row that
 * drops and reweights nothing keeps the reference row (byte-identical either way).
 *
 * M10 P4b5a (plan 2026-10-15-m10-p4b §0.3, review M1): the cell's mix is the buyer's `hull-lines buyerMixes[b][j]`
 * when present (L hulls reach a row only that way), else the `roles.json` mix. Each cell gets two rows, each with its
 * own `len`, `total` and `cum`: `all` (the catalogue's own arrays, as above) and `smOnly` (the same rule without the
 * L-or-above entries, at weight 1 × their mix weight; its fallback has no L either). `hasL` marks an `all` row that
 * holds an L. With `lWeight` 0 or no L in a mix (the shipped data) the two rows are byte-equal.
 */
export function buildCatalogue(
  roles: RolesFile,
  hullIndex: ReadonlyMap<string, number>,
  factionCapacity: number,
  slots: number = CATALOGUE_SLOTS,
  lines: CatalogueLines | null = null,
): Catalogue {
  const rows = factionCapacity * factionCapacity * JOB_COUNT
  const hull = new Int16Array(rows * slots)
  const weight = new Float64Array(rows * slots)
  const cum = new Float64Array(rows * slots)
  const len = new Uint8Array(rows)
  const total = new Float64Array(rows)
  // The reference row per job (tooling 0, buyer 0), then copied to every pair.
  const refMix: SourceMix[] = []
  for (let job = 0; job < JOB_COUNT; job++) {
    const mix = sourceMixOf(mixOf(roles, job), hullIndex, slots, `job ${CATALOGUE_JOBS[job]}`)
    refMix.push(mix)
    const base = job * slots
    let sum = 0
    for (let k = 0; k < mix.len; k++) {
      sum += mix.weight[k]
      hull[base + k] = mix.hull[k]
      weight[base + k] = mix.weight[k]
      cum[base + k] = sum
    }
    len[job] = mix.len
    total[job] = sum
  }
  const stride = JOB_COUNT * slots
  for (let pair = 1; pair < factionCapacity * factionCapacity; pair++) {
    hull.copyWithin(pair * stride, 0, stride)
    weight.copyWithin(pair * stride, 0, stride)
    cum.copyWithin(pair * stride, 0, stride)
    len.copyWithin(pair * JOB_COUNT, 0, JOB_COUNT)
    total.copyWithin(pair * JOB_COUNT, 0, JOB_COUNT)
  }
  // The S/M rows start as their own copy of the reference rows (never views of the `all` arrays).
  const smOnly: CatalogueRows = { hull: hull.slice(), weight: weight.slice(), cum: cum.slice(), len: len.slice(), total: total.slice() }
  const cat: Catalogue = { factionCapacity, slots, hull, weight, cum, len, total, smOnly, hasL: new Uint8Array(rows) }
  if (lines === null) return cat
  const rules = lineRulesOf(lines, factionCapacity, hullIndex, slots)
  for (let t = 0; t < factionCapacity; t++) {
    for (let b = 0; b < factionCapacity; b++) {
      const outlaw = lines.charter.outlaw[b] === 1
      for (let job = 0; job < JOB_COUNT; job++) {
        const own = rules.buyerMix[b * JOB_COUNT + job]
        const src = own ?? refMix[job]
        const row = catalogueRow(cat, t, b, job)
        const cell: Cell = { rules, t, b, job, outlaw, src, copied: own === null }
        writeRow(cat, cat, row, cell, true)
        writeRow(cat, smOnly, row, cell, false)
        cat.hasL[row] = rowHoldsL(cat, rules, row) ? 1 : 0
      }
    }
  }
  return cat
}

/** One (tooling, buyer, job) cell being written (build time only). `copied`: the row already holds `src` (the roles mix). */
interface Cell {
  readonly rules: LineRules
  readonly t: number
  readonly b: number
  readonly job: number
  readonly outlaw: boolean
  readonly src: SourceMix
  readonly copied: boolean
}

/** Hull `h` of the cell's mix enters the row (`withL`: the `all` row; else the `smOnly` row, which drops L). */
function keeps(c: Cell, h: number, withL: boolean): boolean {
  const r = c.rules
  return !retired(r, h) && toolingBuilds(r, c.t, h) && (!c.outlaw || outlawMayBuy(r, h)) && (withL || !isL(r, h))
}

/** Writes one row of the cell over its copy of the reference row (see `buildCatalogue`). Build time only. */
function writeRow(cat: Catalogue, dst: CatalogueRows, row: number, c: Cell, withL: boolean): void {
  const r = c.rules
  const slots = cat.slots
  const base = row * slots
  const factor = (h: number): number => (withL ? bandWeight(r, h) : 1)
  // The reference-row fast path: every pair already holds the reference rows (the copy above).
  let same = c.copied
  for (let k = 0; k < c.src.len && same; k++) same = keeps(c, c.src.hull[k], withL) && factor(c.src.hull[k]) === 1
  if (same) return
  let n = 0
  let sum = 0
  const put = (h: number, w: number): void => {
    sum += w
    dst.hull[base + n] = h
    dst.weight[base + n] = w
    dst.cum[base + n] = sum
    n++
  }
  for (let k = 0; k < c.src.len; k++) {
    const h = c.src.hull[k]
    if (!keeps(c, h, withL)) continue
    const w = c.src.weight[k] * factor(h)
    if (w > 0) put(h, w)
  }
  if (n === 0) {
    // The deadlock fallback: the job's export patterns, 1 : 1 (review m5).
    for (let h = 0; h < r.lines.hulls.hulls.length; h++) {
      if (((r.lines.hullFlags[h] ?? 0) & CATALOGUE_EXPORT_BIT) === 0 || retired(r, h) || !fliesJob(r, h, c.job)) continue
      if ((c.outlaw && !outlawMayBuy(r, h)) || (!withL && isL(r, h))) continue
      const w = factor(h)
      if (!(w > 0)) continue
      if (n === slots) throw new Error(`catalogue: job ${CATALOGUE_JOBS[c.job]} has more than ${slots} export patterns`)
      put(h, w)
    }
    if (n === 0) throw new Error(`catalogue: job ${CATALOGUE_JOBS[c.job]} has no export pattern for tooling ${c.t}, buyer ${c.b}`)
  }
  for (let k = n; k < slots; k++) {
    dst.hull[base + k] = 0
    dst.weight[base + k] = 0
    dst.cum[base + k] = 0
  }
  dst.len[row] = n
  dst.total[row] = sum
}

/** The `all` row holds an L-or-above hull. */
function rowHoldsL(cat: Catalogue, r: LineRules, row: number): boolean {
  for (let k = 0; k < cat.len[row]; k++) if (isL(r, cat.hull[row * cat.slots + k])) return true
  return false
}

/**
 * M10 P4b5a: the `all` row of (tooling, buyer, job) holds an L-or-above hull (an O(1) flag read; no allocation). The
 * draw site's `allowL` starts from it (plan §0.3).
 */
export function rowHasL(cat: Catalogue, tooling: number, buyer: number, job: number): boolean {
  return cat.hasL[(tooling * cat.factionCapacity + buyer) * JOB_COUNT + job] === 1
}

/**
 * Mirrors `pickWeighted` (galaxy/seed.ts): `r = r01 × total`, `if (r < w) return`, `r -= w`, else the last key.
 * M10 P4b5a: walks the `all` row when `allowL`, else the `smOnly` row, each with its own length (no caller passes
 * `allowL` yet). Hot path: allocates nothing.
 */
export function pickCatalogueHull(r01: number, cat: Catalogue, tooling: number, buyer: number, job: number, allowL = false): number {
  const rows: CatalogueRows = allowL ? cat : cat.smOnly
  const row = (tooling * cat.factionCapacity + buyer) * JOB_COUNT + job
  const base = row * cat.slots
  const n = rows.len[row]
  let r = r01 * rows.total[row]
  for (let i = 0; i < n; i++) {
    const w = rows.weight[base + i]
    if (r < w) return rows.hull[base + i]
    r -= w
  }
  return rows.hull[base + n - 1] // float round-off guard
}

/**
 * Mirrors the convoy's `pickHull` (agents/convoy.ts): `r = r01 × total`, the first `r < cum[i]`, else the last.
 * M10 P4b5a: the row choice of `pickCatalogueHull`.
 */
export function pickCatalogueHullCum(r01: number, cat: Catalogue, tooling: number, buyer: number, job: number, allowL = false): number {
  const rows: CatalogueRows = allowL ? cat : cat.smOnly
  const row = (tooling * cat.factionCapacity + buyer) * JOB_COUNT + job
  const base = row * cat.slots
  const n = rows.len[row]
  const r = r01 * rows.total[row]
  for (let i = 0; i < n; i++) if (r < rows.cum[base + i]) return rows.hull[base + i]
  return rows.hull[base + n - 1]
}

const catalogues = new WeakMap<object, Catalogue | null>()

/** The catalogue of `data`, built once per data object; null when `catalogue.json enabled` is false. */
export function catalogueOf(data: StrategicData): Catalogue | null {
  const hit = catalogues.get(data)
  if (hit !== undefined) return hit
  // The rows read `charter.outlaw`, and `setCharterEligible` (founding, `registry.ts`) replaces `state.data`, so this
  // memo builds one new catalogue then. The player slot's rows are equal either way: no roles.json mix names a hull
  // outside the outlaw rule (every roles.json hull is an S/M export pattern, and buyer mixes are keyed by faction, never
  // the player slot; the deadlock test in catalogue.lines.test.ts keeps it so).
  const cat = CATALOGUE_FILE.enabled
    ? buildCatalogue(data.roles, data.hullIndex, data.strategic.caps.factionCapacity, CATALOGUE_SLOTS, data)
    : null
  catalogues.set(data, cat)
  return cat
}

/** Drops the memoised catalogue of `data` (call after mutating `data.roles` in place). */
export function invalidateCatalogue(data: StrategicData): void {
  catalogues.delete(data)
}

/**
 * Test hook (review M6): sets `data`'s catalogue (null = off) whatever `catalogue.json enabled` says, so an A/B test
 * runs on and off on a data copy. No runtime caller.
 */
export function setCatalogueOverride(data: StrategicData, cat: Catalogue | null): void {
  catalogues.set(data, cat)
}

/**
 * The tooling faction of a hull built at station `yard` for `buyer`: the yard's tooling (`StationStore.tooling`, the
 * owner it was built for; a capture keeps it; M10 P3a2). No yard (−1), a tooling outside [0, factionCapacity) or the
 * player slot (it has no catalogue row) uses the buyer, so the row read never goes out of range (an out-of-range read is
 * `undefined`, which would make `r` NaN and silently pick the last hull).
 */
export function toolingOf(state: StrategicState, yard: number, buyer: number): number {
  if (yard < 0) return buyer
  const tooling = state.stations.tooling[yard]
  if (tooling === undefined || tooling >= state.data.strategic.caps.factionCapacity || tooling === PLAYER_SLOT) return buyer
  return tooling
}
